import React from "react";
import "../DesignerPropertiesPanel.css";

const normalizeColorValue = (value, fallback = "#000000") => {
  if (!value || typeof value !== "string") return fallback;

  const trimmed = value.trim();
  const hexColorRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

  if (hexColorRegex.test(trimmed)) return trimmed;
  return fallback;
};

const ColorField = ({ label, value, onTextChange, onPickerChange, fallback }) => {
  const pickerValue = normalizeColorValue(value, fallback);

  return (
    <div className="dp-field">
      <label className="dp-label">{label}</label>

      <div className="dp-color-row">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={fallback}
          className="dp-input"
        />

        <input
          type="color"
          value={pickerValue}
          onChange={(e) => onPickerChange(e.target.value)}
          className="dp-color-input"
        />
      </div>
    </div>
  );
};

const TagProperties = ({
  selectedObject,
  latestTags,
  updateObjectDataField,
  updateObjectStyleField,
}) => {
  const dataJson = selectedObject.data_json || {};
  const styleJson = selectedObject.style_json || {};

  return (
    <div className="dp-section">
      <h4 className="dp-section-title">Content</h4>

      <div className="dp-field">
        <label className="dp-label">Object Mode</label>
        <select
          value={dataJson.object_mode || "monitor"}
          onChange={(e) =>
            updateObjectDataField(selectedObject.id, "object_mode", e.target.value)
          }
          className="dp-select"
        >
          <option value="monitor">Monitoring</option>
          <option value="control">Control / Write</option>
        </select>
      </div>

      <div className="dp-field">
        <label className="dp-label">Tag Mode</label>
        <select
          value={dataJson.tag_mode || "manual"}
          onChange={(e) =>
            updateObjectDataField(selectedObject.id, "tag_mode", e.target.value)
          }
          className="dp-select"
        >
          <option value="manual">Manual</option>
          <option value="realtime">Realtime</option>
        </select>
      </div>

      <div className="dp-field">
        <label className="dp-label">Prefix</label>
        <input
          type="text"
          value={dataJson.prefix || ""}
          onChange={(e) =>
            updateObjectDataField(selectedObject.id, "prefix", e.target.value)
          }
          className="dp-input"
        />
      </div>

      <div className="dp-field">
        <label className="dp-label">Suffix</label>
        <input
          type="text"
          value={dataJson.suffix || ""}
          onChange={(e) =>
            updateObjectDataField(selectedObject.id, "suffix", e.target.value)
          }
          className="dp-input"
        />
      </div>

      {(dataJson.tag_mode || "manual") === "manual" && (
        <div className="dp-field">
          <label className="dp-label">Value</label>
          <input
            type="text"
            value={dataJson.value || ""}
            onChange={(e) =>
              updateObjectDataField(selectedObject.id, "value", e.target.value)
            }
            className="dp-input"
          />
        </div>
      )}

      {(dataJson.tag_mode || "manual") === "realtime" && (
        <>
          <div className="dp-field">
            <label className="dp-label">Real Tag Name</label>
            <select
              value={dataJson.tag_name || ""}
              onChange={(e) =>
                updateObjectDataField(selectedObject.id, "tag_name", e.target.value)
              }
              className="dp-select"
            >
              <option value="">Select real tag</option>
              {(latestTags || []).map((tag) => (
                <option key={tag.tag_name} value={tag.tag_name}>
                  {tag.tag_name}
                </option>
              ))}
            </select>
          </div>

          <div className="dp-field">
            <label className="dp-label">Live Preview</label>
            <input
              disabled
              value={
                (latestTags || []).find(
                  (tag) => tag.tag_name === (dataJson.tag_name || "")
                )?.last_value ?? "N/A"
              }
              className="dp-input"
            />
          </div>
        </>
      )}

      <div className="dp-field">
        <label className="dp-label">Unit</label>
        <input
          type="text"
          value={dataJson.unit || ""}
          onChange={(e) =>
            updateObjectDataField(selectedObject.id, "unit", e.target.value)
          }
          className="dp-input"
        />
      </div>

      {(dataJson.object_mode || "monitor") === "control" && (
        <>
          <div className="dp-field">
            <label className="dp-label">Control Type</label>
            <select
              value={dataJson.control_type || "digital"}
              onChange={(e) =>
                updateObjectDataField(selectedObject.id, "control_type", e.target.value)
              }
              className="dp-select"
            >
              <option value="digital">Digital</option>
              <option value="analog">Analog</option>
            </select>
          </div>

          <div className="dp-info-box">
            مقدار کنترل از مقدار زنده تگ خوانده می‌شود و مقدار پیش‌فرض ذخیره نمی‌شود.
          </div>
        </>
      )}

      <div className="dp-section">
        <h4 className="dp-section-title">Tag Background</h4>

        <div className="dp-field">
          <label className="dp-checkbox-label">
            <input
              type="checkbox"
              checked={styleJson.hasBackground || false}
              onChange={(e) =>
                updateObjectStyleField(
                  selectedObject.id,
                  "hasBackground",
                  e.target.checked
                )
              }
            />
            Enable Background
          </label>
        </div>

        {styleJson.hasBackground && (
          <>
            <ColorField
              label="Background Color"
              value={styleJson.backgroundColor || ""}
              fallback="#e0f2fe"
              onTextChange={(value) =>
                updateObjectStyleField(
                  selectedObject.id,
                  "backgroundColor",
                  value
                )
              }
              onPickerChange={(value) =>
                updateObjectStyleField(
                  selectedObject.id,
                  "backgroundColor",
                  value
                )
              }
            />

            <div className="dp-grid-2">
              <div className="dp-field">
                <label className="dp-label">Border Radius</label>
                <input
                  type="number"
                  value={styleJson.borderRadius ?? 8}
                  onChange={(e) =>
                    updateObjectStyleField(
                      selectedObject.id,
                      "borderRadius",
                      Number(e.target.value) || 0
                    )
                  }
                  className="dp-input"
                />
              </div>

              <div className="dp-field">
                <label className="dp-label">Padding</label>
                <input
                  type="number"
                  value={styleJson.padding ?? 6}
                  onChange={(e) =>
                    updateObjectStyleField(
                      selectedObject.id,
                      "padding",
                      Number(e.target.value) || 0
                    )
                  }
                  className="dp-input"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TagProperties;